import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiTrash2, FiClock, FiMapPin, FiPhoneForwarded } from 'react-icons/fi';

export interface CallRule {
  id: string;
  type: 'time-based' | 'geo-based' | 'failover';
  config: any;
  targetNumber: string;
}

interface CallRoutingSettingsProps {
  phoneNumberId: string;
  initialRules?: CallRule[] | null;
}

const CallRoutingSettings: React.FC<CallRoutingSettingsProps> = ({ phoneNumberId, initialRules = [] }) => {
  const [rules, setRules] = useState<CallRule[]>(initialRules || []);
  const [newRule, setNewRule] = useState<Omit<CallRule, 'id'>>({ type: 'failover', targetNumber: '', config: {} });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setRules(initialRules || []);
  }, [initialRules]);

  const handleAddRule = () => {
    if (!newRule.targetNumber) {
      toast.error('Please enter a target phone number.');
      return;
    }
    setRules([...rules, { ...newRule, id: `new-${Date.now()}` }]);
    setNewRule({ type: 'failover' as const, targetNumber: '', config: {} });
  };

  const handleRemoveRule = (id: string) => {
    setRules(rules.filter(rule => rule.id !== id));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      // Assuming a single forwarding number for now as per the existing backend endpoint
      const primaryRule = rules.find(r => r.type === 'failover');
      
      await api.put(`/phone-numbers/${phoneNumberId}/forwarding`, {
        forwardingEnabled: rules.length > 0,
        forwardingNumber: primaryRule ? primaryRule.targetNumber : null,
        forwardingRules: rules,
      });
      toast.success('Call routing settings saved!');
    } catch (error) {
      toast.error('Failed to save settings.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRuleIcon = (type: CallRule['type']) => {
    switch (type) {
      case 'time-based': return <FiClock className="text-purple-500" />;
      case 'geo-based': return <FiMapPin className="text-blue-500" />;
      case 'failover':
      default:
        return <FiPhoneForwarded className="text-green-500" />;
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-white mb-4">Call Routing Settings</h2>
      
      <div className="space-y-4 mb-6">
        {rules.map((rule, index) => (
          <div key={rule.id} className="flex items-center justify-between bg-gray-700 p-3 rounded">
            <div className="flex items-center gap-3">
              {getRuleIcon(rule.type)}
              <span className="text-gray-300">Rule #{index + 1}:</span>
              <span className="text-white font-mono">{rule.type}</span>
              <span className="text-gray-300">to</span>
              <span className="text-white font-mono">{rule.targetNumber}</span>
            </div>
            <button onClick={() => handleRemoveRule(rule.id)} className="text-red-500 hover:text-red-400">
              <FiTrash2 />
            </button>
          </div>
        ))}
      </div>

      <div className="bg-gray-700 p-4 rounded-lg flex items-center gap-4">
        <select
          value={newRule.type}
          onChange={e => setNewRule({ ...newRule, type: e.target.value as CallRule['type'] })}
          className="bg-gray-900 text-white border border-gray-600 rounded px-3 py-2"
        >
          <option value="failover">Failover</option>
          <option value="time-based" disabled>Time-based (coming soon)</option>
          <option value="geo-based" disabled>Geo-based (coming soon)</option>
        </select>
        <input
          type="tel"
          value={newRule.targetNumber}
          onChange={e => setNewRule({ ...newRule, targetNumber: e.target.value })}
          placeholder="Enter target phone number"
          className="flex-grow bg-gray-900 text-white border border-gray-600 rounded px-3 py-2"
        />
        <button onClick={handleAddRule} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded inline-flex items-center">
          <FiPlus className="mr-2" /> Add Rule
        </button>
      </div>

      <div className="mt-6 text-right">
        <button onClick={handleSaveSettings} disabled={isLoading} className="bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-4 rounded">
          {isLoading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default CallRoutingSettings;
